// ══════════════════════════════════════════════════════════════════════════
// salesData.js — parsing + reconciliation for the Sales and Audit tabs
//
// Everything here is pure logic: no React, no Supabase, no DOM. It takes the
// workbooks/rows the existing upload handler already produces and returns
// plain data. That keeps it testable and keeps PricingDashboard.jsx untouched
// apart from the wiring described at the bottom of this file.
//
// Validated against real exports for all 13 stores, weeks 2026-07-20 and
// 2026-07-27 (2,856 sales rows, 5,004 catalog items, 2,935 catalog-only rows).
// ══════════════════════════════════════════════════════════════════════════

import * as XLSX from "xlsx";

// ── Store account numbers, used to slice the multi-store Sales Price Export.
export const STORE_ACCT = {
  BO: "100001005", SH: "100001010", KP: "100001003", AV: "100001001",
  WP: "100001004", CH: "100001006", DA: "100001007", SV: "100001011",
  LA: "100001002", TO: "800001000", SG: "800001001", DM: "800001002",
  EC: "800001003",
};
export const ACCT_STORE = Object.fromEntries(
  Object.entries(STORE_ACCT).map(([s, a]) => [a, s])
);

// Values that look like data but aren't. Excel formula errors, zero-as-blank,
// and Boston's literal "BATCH" placeholder in the Catapult ID column.
const JUNK = new Set(["#N/A", "#REF!", "#VALUE!", "#NAME?", "0", "", "-", "NONE", "BATCH"]);

/** Normalise an ID cell: trims, strips a trailing ".0" from numeric cells, and
 *  treats formula errors / placeholders as absent. */
export function cleanId(v) {
  if (v == null) return "";
  let s = String(v).trim();
  if (s.endsWith(".0") && /^\d+\.0$/.test(s)) s = s.slice(0, -2);
  return JUNK.has(s.toUpperCase()) ? "" : s;
}

// Match on the raw value and on the zero-stripped value, because D365 IDs
// appear both padded ("00675150") and unpadded ("675150") across systems.
const idKeys = (id) => (id ? [id, id.replace(/^0+/, "")] : []);

// ══════════════════════════════════════════════════════════════════════════
// 1. SALES REPORT  (Catapult export — "Product Sales_MMDDYY.xlsx" or .csv)
// ══════════════════════════════════════════════════════════════════════════

/** True if this workbook looks like the Catapult product-sales export. */
export function looksLikeSalesReport(wb) {
  try {
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    for (let i = 0; i < Math.min(rows.length, 6); i++) {
      const h = rows[i].map((c) => String(c).trim().toUpperCase());
      if (h.includes("PRODUCT ID") && h.includes("QUANTITY SOLD") && h.includes("NET AMOUNT")) return true;
    }
  } catch { /* not a readable workbook */ }
  return false;
}

// Pseudo-items that are fees, not products. Instacart markup lines are
// ~1.8% of LA revenue and would distort every ranking, so they're dropped.
const isPseudoItem = (pid) => /^9391\d\d$/.test(pid) || /^93915[0-9]$/.test(pid);

/**
 * Parse the sales report into { [store]: { [catapultId]: row } }.
 * Handles both .xlsx and .csv (SheetJS reads both into a workbook).
 */
export function parseSalesReport(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  // Find the header row rather than assuming row 0 — exports have carried a
  // title row before, and catalogs have taught us not to trust position.
  let hi = -1, H = null;
  for (let i = 0; i < Math.min(raw.length, 8); i++) {
    const h = raw[i].map((c) => String(c).trim().toUpperCase());
    if (h.includes("PRODUCT ID") && h.includes("NET AMOUNT")) { hi = i; H = h; break; }
  }
  if (hi < 0) return {};

  const col = (name) => H.indexOf(name);
  const cStore = col("STORE"), cOutlet = col("SALES OUTLET"), cPid = col("PRODUCT ID"),
        cName = col("PRODUCT NAME"), cCat = col("CATEGORY"), cSub = col("SUBCATEGORY"),
        cQty = col("QUANTITY SOLD"), cAmt = col("NET AMOUNT");

  const out = {}; let skippedPseudo = 0;
  for (let i = hi + 1; i < raw.length; i++) {
    const r = raw[i];
    const store = String(r[cStore] ?? "").trim().toUpperCase();
    const pid = cleanId(r[cPid]);
    if (!store || !pid) continue;
    if (isPseudoItem(pid)) { skippedPseudo++; continue; }
    // Guard in case the export ever widens beyond produce.
    const outlet = cOutlet >= 0 ? String(r[cOutlet] ?? "").trim().toUpperCase() : "PRODUCE";
    if (outlet && outlet !== "PRODUCE") continue;

    const qty = Number(r[cQty]) || 0;
    const amt = Number(r[cAmt]) || 0;
    (out[store] = out[store] || {})[pid] = {
      pid,
      name: String(r[cName] ?? "").trim(),
      category: cCat >= 0 ? String(r[cCat] ?? "").trim() : "",
      sub: cSub >= 0 ? String(r[cSub] ?? "").trim() : "",
      qty: Math.round(qty * 1000) / 1000,
      amt: Math.round(amt * 100) / 100,
    };
  }
  out._meta = { skippedPseudo };
  return out;
}

// ══════════════════════════════════════════════════════════════════════════
// 2. CATALOG → CATAPULT ID INDEX
//
// Column positions differ per store, so every column is located by header
// NAME, and the header row itself is detected. Observed real variation:
//   • Catapult ID at column 13 (LA) or 14 (everyone else) or 7 (Canada)
//   • header on row 0, 2, or 3 depending on the file
//   • D365 column named "D365 Item No." or "Product ID" (Boston, Silicon Valley)
//   • department column named CATEGORY / Department / Product
//   • sheet named "MASTER CATALOG - FRESH", "MASTER CATALOG", or "CAN MASTER…"
//   • Silicon Valley ships a stale duplicate "…FRESH (2)" full of #REF!, so the
//     sheet is chosen by how much produce it actually contains, not by name.
// ══════════════════════════════════════════════════════════════════════════

function findHeaderRow(rows) {
  let best = 0, bi = 0;
  for (let i = 0; i < Math.min(rows.length, 12); i++) {
    const cells = (rows[i] || []).map((c) => String(c ?? "").trim().toUpperCase());
    const score = ["BOH", "CATAPULT", "STATUS", "CATEGORY", "DEPARTMENT", "PRODUCT"]
      .reduce((n, kw) => n + (cells.some((c) => c.includes(kw)) ? 1 : 0), 0);
    if (score > best) { best = score; bi = i; }
  }
  return bi;
}

function locateColumns(hdr) {
  const U = hdr.map((h) => String(h ?? "").trim().toUpperCase());
  const find = (pred) => { for (let i = 0; i < U.length; i++) if (pred(U[i])) return i; return -1; };
  const or = (...ix) => ix.find((i) => i >= 0) ?? -1;
  return {
    dept:   find((h) => h === "CATEGORY" || h === "DEPARTMENT" || h === "PRODUCT"),
    boh:    find((h) => h.includes("BOH")),
    cid:    find((h) => h.includes("CATAPULT")),
    d365:   or(find((h) => h.includes("D365") && h.includes("ITEM")),
               find((h) => h.includes("ITEM NO")),
               find((h) => h === "PRODUCT ID")),
    name:   or(find((h) => h.includes("PRODUCT NAME")), find((h) => h.includes("PRODUCT DESC"))),
    status: find((h) => h.includes("STATUS") && !h.includes("ADDITIONAL")),
  };
}

/**
 * Extract produce/RTL/in-stock catalog items and their Catapult IDs.
 * @param {object} wb        SheetJS workbook
 * @param {string[]} stores  store codes this file covers
 * @param {boolean} perStoreFlags  true for the Canada file, which carries one
 *                                 row per item with TO/SG/DM/EC flag columns
 * @returns {{ meta:object, byStore:{ [store]: Array<{d365,cid,name,status}> } }}
 */
export function parseCatalogCatapult(wb, stores, perStoreFlags = false) {
  const cands = wb.SheetNames.filter((n) => n.toUpperCase().includes("MASTER CATALOG"));
  let pick = null;

  for (const nm of cands) {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[nm], { header: 1, defval: "" });
    if (!rows.length) continue;
    const hi = findHeaderRow(rows);
    const c = locateColumns(rows[hi] || []);
    if (c.dept < 0 || c.boh < 0 || c.cid < 0) continue;
    // Count real produce rows; the biggest wins. This is what rejects the
    // stale "(2)" duplicate sheets.
    let n = 0;
    for (let i = hi + 1; i < rows.length; i++) {
      const r = rows[i];
      if (!String(r[c.dept] ?? "").toUpperCase().includes("PRODUCE")) continue;
      if (!String(r[c.boh] ?? "").toUpperCase().includes("RTL")) continue;
      n++;
    }
    if (!pick || n > pick.n) pick = { n, nm, hi, c, rows };
  }
  if (!pick) return { meta: { error: "no usable MASTER CATALOG sheet" }, byStore: {} };

  const { nm, hi, c, rows } = pick;
  const hdrRaw = (rows[hi] || []).map((h) => String(h ?? "").trim());
  const flagIx = {};
  if (perStoreFlags) stores.forEach((s) => { const i = hdrRaw.indexOf(s); if (i >= 0) flagIx[s] = i; });

  const byStore = {}; stores.forEach((s) => (byStore[s] = []));
  // Dual-vendor items occupy two catalog rows for the same product. Without
  // de-duplication Boston reports 1,006 items instead of 900 and the
  // "not sold / in catalog" bucket inflates by the difference.
  const seenKey = {}; stores.forEach((s) => (seenKey[s] = new Set()));
  const stats = { rows: 0, noCid: 0, duplicates: 0, statusCounts: {} };

  for (let i = hi + 1; i < rows.length; i++) {
    const r = rows[i];
    if (!String(r[c.dept] ?? "").toUpperCase().includes("PRODUCE")) continue;
    if (!String(r[c.boh] ?? "").toUpperCase().includes("RTL")) continue;
    const status = c.status >= 0 ? String(r[c.status] ?? "").trim().toUpperCase() : "";
    if (status.includes("BACK")) continue;          // backup rows aren't the sell item

    const d365 = c.d365 >= 0 ? cleanId(r[c.d365]) : "";
    const cid  = cleanId(r[c.cid]);
    const name = c.name >= 0 ? String(r[c.name] ?? "").trim() : "";
    if (!d365 && !cid) continue;
    stats.rows++;
    if (!cid) stats.noCid++;
    stats.statusCounts[status || "(blank)"] = (stats.statusCounts[status || "(blank)"] || 0) + 1;

    const rec = { d365, cid, name, status };
    const key = d365 || "C" + cid;
    const targets = perStoreFlags
      ? Object.entries(flagIx).filter(([, ix]) => cleanId(r[ix])).map(([s]) => s)
      : stores;
    targets.forEach((s) => {
      if (seenKey[s].has(key)) { stats.duplicates++; return; }
      seenKey[s].add(key);
      byStore[s].push(rec);
    });
  }
  return { meta: { sheet: nm, headerRow: hi, cols: c, ...stats }, byStore };
}

/** Build lookup indexes for one store's catalog. */
export function indexCatalog(items) {
  const byCid = {}, byD365 = {};
  for (const rec of items || []) {
    if (rec.cid) byCid[rec.cid] = rec;
    idKeys(rec.d365).forEach((k) => { byD365[k] = rec; });
  }
  return { byCid, byD365, items: items || [] };
}

// ══════════════════════════════════════════════════════════════════════════
// 3. RECONCILIATION — the two-pass match
//
//   pass 1  sales Product Id === catalog Catapult ID          → "matched"
//   pass 2  sales Product Id === catalog D365 number          → "idMismatch"
//           (the scale rings the D365 while the catalog assigns a shortcode;
//            an exact match, not a guess — a mechanical fix list)
//   else                                                      → "notInCatalog"
// ══════════════════════════════════════════════════════════════════════════

export const STATUS = { MATCHED: "matched", ID_MISMATCH: "idMismatch", NOT_IN_CATALOG: "notInCatalog" };

export function resolveStatus(pid, idx) {
  if (idx.byCid[pid]) return { status: STATUS.MATCHED, rec: idx.byCid[pid] };
  for (const k of idKeys(pid)) {
    if (idx.byD365[k]) return { status: STATUS.ID_MISMATCH, rec: idx.byD365[k] };
  }
  return { status: STATUS.NOT_IN_CATALOG, rec: null };
}

// ══════════════════════════════════════════════════════════════════════════
// 4. LIST PRICE from the Sales Price Export
//
// Use the "Price decimal" column: "Amount in transaction currency" is that
// value times "Price unit" (100 for most rows, 1 for a few). A price under
// $0.01 is a $0.001 placeholder meaning "never priced", not a real price —
// LA alone had 54 of them, so it gets its own state rather than being charted.
// ══════════════════════════════════════════════════════════════════════════

export function parseListPrices(wb) {
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  if (!raw.length) return {};
  const H = raw[0].map((h) => String(h).trim().toUpperCase());
  const cAcct = H.indexOf("ACCOUNT SELECTION"),
        cItem = H.indexOf("ITEM"),
        cDec  = H.indexOf("PRICE DECIMAL"),
        cAmt  = H.indexOf("AMOUNT IN TRANSACTION CURRENCY"),
        cUnit = H.indexOf("PRICE UNIT");
  const out = {};
  for (let i = 1; i < raw.length; i++) {
    const r = raw[i];
    const store = ACCT_STORE[String(r[cAcct] ?? "").trim()];
    if (!store) continue;
    const item = cleanId(r[cItem]);
    if (!item) continue;
    let p = cDec >= 0 ? Number(r[cDec]) : NaN;
    if (!isFinite(p) && cAmt >= 0) {                     // fall back to amount ÷ unit
      const u = Number(r[cUnit]) || 1;
      p = Number(r[cAmt]) / (u || 1);
    }
    if (!isFinite(p)) continue;
    const bucket = (out[store] = out[store] || { price: {}, placeholder: new Set() });
    if (p < 0.01) idKeys(item).forEach((k) => bucket.placeholder.add(k));
    else idKeys(item).forEach((k) => { bucket.price[k] = p; });
  }
  return out;
}

export function lookupListPrice(store, keys, prices) {
  const b = prices?.[store];
  if (!b) return { listPrice: null, placeholder: false };
  for (const k of keys) if (b.price[k] != null) return { listPrice: b.price[k], placeholder: false };
  return { listPrice: null, placeholder: keys.some((k) => b.placeholder.has(k)) };
}

// ══════════════════════════════════════════════════════════════════════════
// 5. PRICE GAP — captured vs list
//
// Captured price is net ÷ quantity. On weight items that division carries
// sub-cent noise (Lemon Org came out $1.1900 one week and $1.1870 the next),
// so both sides are rounded to cents and anything under 3¢ apart reports as
// zero. Without that guard the noise shows up as a fake price movement.
// ══════════════════════════════════════════════════════════════════════════

export const toCents = (v) => (v == null ? null : Math.round(v * 100) / 100);

export function priceGapPct({ qty, amt, listPrice, placeholder }) {
  if (placeholder || listPrice == null || !qty || amt == null || qty === 0) return null;
  const captured = toCents(amt / qty), list = toCents(listPrice);
  if (Math.abs(captured - list) < 0.03) return 0;
  return ((captured - list) / list) * 100;
}

// ══════════════════════════════════════════════════════════════════════════
// 6. BUILD — one pass producing both the Sales rows and the Audit buckets
//
// The Audit tab's two "sold" buckets ARE the Sales rows, so they're derived
// rather than stored twice. Only catalogOnly is additional data.
// ══════════════════════════════════════════════════════════════════════════

/**
 * @param {object} args
 *   stores        string[]                     store codes to process
 *   salesByStore  { [store]: { [pid]: row } }  from parseSalesReport
 *   catalogByStore{ [store]: item[] }          from parseCatalogCatapult
 *   prices        from parseListPrices
 * @returns {{ sales: object[], catalogOnly: object[], summary: object }}
 */
export function buildSalesAndAudit({ stores, salesByStore, catalogByStore, prices }) {
  const sales = [], catalogOnly = [], summary = {};

  for (const store of stores) {
    const idx = indexCatalog(catalogByStore?.[store]);
    const hasCatalog = idx.items.length > 0;
    const srows = salesByStore?.[store] || {};
    const claimed = new Set();
    const s = { matched: 0, idMismatch: 0, notInCatalog: 0, catalogOnly: 0, noCatalog: !hasCatalog };

    for (const pid of Object.keys(srows)) {
      const row = srows[pid];
      const { status, rec } = hasCatalog
        ? resolveStatus(pid, idx)
        : { status: null, rec: null };      // unknown until the catalog loads
      if (rec) claimed.add(rec.d365 || "C" + rec.cid);
      const keys = rec?.d365 ? idKeys(rec.d365) : idKeys(pid);
      const { listPrice, placeholder } = lookupListPrice(store, keys, prices);
      sales.push({
        store, pid, d365: rec?.d365 || "", name: row.name,
        catalogName: rec && rec.name && rec.name.toUpperCase() !== row.name.toUpperCase() ? rec.name : "",
        sub: row.sub, qty: row.qty, amt: row.amt, listPrice, placeholder,
        status, catalogStatus: rec?.status || "",
      });
      if (status) s[status]++;
    }

    // Catalog items no sales row claimed → "not sold / in catalog"
    for (const rec of idx.items) {
      const key = rec.d365 || "C" + rec.cid;
      if (claimed.has(key)) continue;
      const { listPrice, placeholder } = lookupListPrice(store, idKeys(rec.d365), prices);
      catalogOnly.push({
        store, pid: rec.cid || "", d365: rec.d365 || "", name: rec.name,
        listPrice, placeholder, catalogStatus: rec.status || "",
      });
      s.catalogOnly++;
    }
    summary[store] = s;
  }
  return { sales, catalogOnly, summary };
}

/** Audit bucket keys, derived — never stored separately. */
export const BUCKET = { SOLD_IN: "soldInCatalog", NOT_SOLD_IN: "notSoldInCatalog", SOLD_NOT_IN: "soldNotInCatalog" };
export function bucketOf(row) {
  if (row.catalogStatus !== undefined && row.qty === undefined) return BUCKET.NOT_SOLD_IN;
  return row.status === STATUS.NOT_IN_CATALOG ? BUCKET.SOLD_NOT_IN : BUCKET.SOLD_IN;
}

// ══════════════════════════════════════════════════════════════════════════
// 6a. SALES WEEK DATING
//
// The filename carries the MONDAY THAT STARTS the week the data covers —
// the same rule as vendor price lists, catalogs and the Sales Price Export.
// One convention across every input, so sales lines up with cost natively.
//
//   Product Sales_080326.xlsx  →  week 2026-08-03, covering 08-03 … 08-09
//
// Because the export is produced for COMPLETED weeks, a file that resolves to
// the current (in-progress) week or a future week is almost certainly named
// with the export date instead of the week start. That's the one mis-dating
// this can actually detect, so it's surfaced as a warning rather than silently
// shifting the data — a silent one-week shift would look like real movement.
// ══════════════════════════════════════════════════════════════════════════

/** Monday of the week containing `date` (weeks run Monday–Sunday). */
export function mondayOf(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = d.getDay();                 // 0=Sun … 6=Sat
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return d;
}

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Resolve the week a sales report covers from its filename.
 * Accepts MMDDYY / MMDDYYYY / MM-DD-YY / YYYY-MM-DD separated by _ - . or space.
 * @returns {{ week, covers:[start,end], warning:string|null } | null}
 */
export function salesWeekFromFilename(filename, today = new Date()) {
  const name = String(filename || "");
  let y, m, d;

  let mt = name.match(/(20\d{2})[-_. ](\d{1,2})[-_. ](\d{1,2})/);      // YYYY-MM-DD
  if (mt) { y = +mt[1]; m = +mt[2]; d = +mt[3]; }
  if (y == null) {
    mt = name.match(/(\d{1,2})[-_. ](\d{1,2})[-_. ](20\d{2}|\d{2})/);  // MM-DD-YY(YY)
    if (mt) { m = +mt[1]; d = +mt[2]; y = +mt[3]; if (y < 100) y += 2000; }
  }
  if (y == null) {
    mt = name.match(/(?:^|[^\d])(\d{2})(\d{2})(\d{2})(?:[^\d]|$)/);    // MMDDYY, e.g. 080326
    if (mt) { m = +mt[1]; d = +mt[2]; y = 2000 + +mt[3]; }
  }
  if (y == null || !m || !d || m > 12 || d > 31) return null;

  const raw = new Date(y, m - 1, d);
  if (isNaN(raw)) return null;

  const week = mondayOf(raw);                 // tolerate a non-Monday date
  const end = new Date(week); end.setDate(end.getDate() + 6);
  const thisWeek = mondayOf(today);

  let warning = null;
  if (week.getTime() > thisWeek.getTime()) {
    warning = "This file's week is in the future — check the filename date.";
  } else if (week.getTime() === thisWeek.getTime()) {
    warning = "This resolves to the current, in-progress week. Sales reports cover " +
              "completed weeks, so the filename may carry the export date instead of " +
              "the Monday the week starts.";
  } else if (raw.getDay() !== 1) {
    warning = "Filename date isn't a Monday; using " + iso(week) + " (that week's Monday).";
  }
  return { week: iso(week), covers: [iso(week), iso(end)], warning };
}


//
// A. Weekly inputs. Nothing new beyond the sales report; the catalogs and the
//    Sales Price Export you already upload for Pricing supply the rest.
//      • catalogs             → Catapult ID ↔ D365 bridge + full item list
//      • Sales Price Export   → list price (per store, by account number)
//      • Product Sales_MMDDYY → NEW
//
// B. File routing. In handleFiles, add ONE branch before the generic checks:
//      } else if (looksLikeSalesReport(wb) || fn.includes("PRODUCT SALES")) {
//        salesReportFiles.push({ parsed: parseSalesReport(wb), fileDate });
//
//    Catalog parsing already happens; capture the Catapult index alongside it:
//      const { byStore } = parseCatalogCatapult(wb, [abbr]);          // US
//      const { byStore } = parseCatalogCatapult(wb, CAN_STORES, true); // Canada
//
// C. Storage keys (Supabase app_state), matching the existing convention:
//      ep_salesWeeks      { [store]: { [monday]: { [pid]: {qty,amt,name,sub} } } }
//      ep_catalogIndex    { [store]: item[] }   ← refreshed on each catalog upload
//    Sales retention should be LONGER than pricing's 8 weeks — 26 is the target,
//    since two weeks can't distinguish a dead item from a seasonal one.
//
// D. Dating. Reuse parseDateFromFilename + snapToMonday so sales lands in the
//    same Monday buckets as cost/price. SETTLE THIS FIRST: if the filename
//    carries the export date rather than the Monday of the week the data
//    covers, every sales week sits one bucket ahead of its true week and
//    misaligns against cost. See the note in chat.
// ══════════════════════════════════════════════════════════════════════════
